import React from "react";
import { Eye, UserCheck, Music } from "lucide-react";
import UnifiedParticipantsTable from "@/components/unified-participants-table";
import UnifiedOnStageTable from "@/components/unified-on-stage-table";
import SelectedForStageTable from "@/components/selected-for-stage-table";
import ConsiderationTable from "@/components/consideration-table";
import type { Performer, JamNight } from "@shared/schema";

interface KanbanViewProps {
  performers: Performer[];
  jamNightId: number;
  jamNight?: JamNight;
  isEventCompleted: boolean;
  showParticipantsInKanban: boolean;
  showConsiderationInKanban?: boolean;
}

export function KanbanView({
  performers,
  jamNightId,
  jamNight,
  isEventCompleted,
  showParticipantsInKanban,
  showConsiderationInKanban = true,
}: KanbanViewProps) {
  // Determine grid columns based on what's visible
  const getGridColumns = () => {
    if (showParticipantsInKanban && showConsiderationInKanban) {
      return 'lg:grid-cols-4';
    } else if (showParticipantsInKanban || showConsiderationInKanban) {
      return 'lg:grid-cols-3';
    } else {
      return 'lg:grid-cols-2';
    }
  };

  return (
    <div 
      className={`grid grid-cols-1 ${getGridColumns()} gap-4 -mx-4 sm:-mx-6 lg:-mx-32 xl:-mx-48 px-4`}
      style={{ height: 'calc(100vh - 200px)' }}
    >
      {/* Column 1: Participants (conditionally shown) */}
      {showParticipantsInKanban && (
        <div className="lg:col-span-1 flex flex-col h-full overflow-hidden">
          <div className="flex-1 overflow-y-auto overflow-x-hidden rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-2" style={{ minHeight: 0 }}>
            <UnifiedParticipantsTable
              performers={performers}
              jamNightId={jamNightId}
              jamNight={jamNight}
              mode="jam-night"
              disabled={isEventCompleted}
              forceMobileView={true}
            />
          </div>
        </div>
      )}

      {/* Column 2: Consideration (conditionally shown) */}
      {showConsiderationInKanban && (
        <div className="lg:col-span-1 flex flex-col h-full overflow-hidden">
          <div className="flex-1 overflow-y-auto overflow-x-hidden rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-2" style={{ minHeight: 0 }}>
            {(() => {
              const hasConsideration = performers.some(p => p.status === 'consideration');
              if (!hasConsideration) {
                return (
                  <div className="bg-white/30 backdrop-blur-xl rounded-2xl shadow-lg border border-white/30 p-8 text-center">
                    <Eye className="h-12 w-12 mx-auto mb-3 text-yellow-600 opacity-50" />
                    <h3 className="text-lg font-medium text-slate-700/70">Consideration</h3>
                    <p className="text-sm text-slate-500/70 mt-1">No performers here yet</p>
                  </div>
                );
              }
              return (
                <ConsiderationTable
                  performers={performers}
                  jamNightId={jamNightId}
                  disabled={isEventCompleted}
                  forceMobileView={true}
                />
              );
            })()}
          </div>
        </div>
      )}

      {/* Column 3: Up Next */}
      <div className="lg:col-span-1 flex flex-col h-full overflow-hidden">
        <div className="flex-1 overflow-y-auto overflow-x-hidden rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-2" style={{ minHeight: 0 }}>
          {(() => {
            const hasSelected = performers.some(p => p.status === 'selected');
            if (!hasSelected) {
              return (
                <div className="bg-white/30 backdrop-blur-xl rounded-2xl shadow-lg border border-white/30 p-8 text-center">
                  <UserCheck className="h-12 w-12 mx-auto mb-3 text-blue-600 opacity-50" />
                  <h3 className="text-lg font-medium text-slate-700/70">Selected</h3>
                  <p className="text-sm text-slate-500/70 mt-1">No performers here yet</p>
                </div>
              );
            }
            return (
              <SelectedForStageTable
                performers={performers}
                jamNightId={jamNightId}
                disabled={isEventCompleted}
                forceMobileView={true}
              />
            );
          })()}
        </div>
      </div>

      {/* Column 4: On Stage */}
      <div className="lg:col-span-1 flex flex-col h-full overflow-hidden">
        <div className="flex-1 overflow-y-auto overflow-x-hidden rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-2" style={{ minHeight: 0 }}>
          {(() => {
            const hasOnStage = performers.some(p => p.status === 'on_stage' || p.status === 'pending_removal');
            if (!hasOnStage) {
              return (
                <div className="bg-white/30 backdrop-blur-xl rounded-2xl shadow-lg border border-white/30 p-8 text-center">
                  <Music className="h-12 w-12 mx-auto mb-3 text-green-600 opacity-50" />
                  <h3 className="text-lg font-medium text-slate-700/70">On Stage</h3>
                  <p className="text-sm text-slate-500/70 mt-1">No performers here yet</p>
                </div>
              );
            }
            if (jamNight) {
              return (
                <UnifiedOnStageTable
                  performers={performers}
                  jamNightId={jamNightId}
                  jamNight={jamNight}
                  disabled={isEventCompleted}
                  mode="jam-night"
                  forceMobileView={true}
                />
              );
            }
            return null;
          })()}
        </div>
      </div>
    </div>
  );
}